import { useState } from "react";
import api from "../lib/api";
import { Envelope, ArrowRight } from "@phosphor-icons/react";

/**
 * Prominent newsletter capture block — designed to sit *above* the Footer so it
 * gets the spotlight before the visitor falls into the footer fine-print zone.
 *
 * Brand-consistent (ink background, brass accent), with a single-line capture
 * and minimal copy. Falls back to the Footer signup if the visitor scrolls past.
 */
export default function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | success | error

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    try {
      await api.post("/public/newsletter", { email: email.trim(), name: name.trim() });
      setState("success");
      setEmail(""); setName("");
    } catch {
      setState("error");
    }
  };

  return (
    <section
      className="bg-ms-ink text-ms-cream py-20 md:py-24 px-6 md:px-12 border-t border-ms-edge/20"
      data-testid="newsletter-cta"
    >
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-16 items-center">
          {/* Left — pitch */}
          <div>
            <p className="label-eyebrow text-ms-brass text-xs">First to know</p>
            <h2 className="serif text-4xl md:text-5xl text-ms-cream mt-3 leading-[1.05]">
              New mysteries drop monthly.
            </h2>
            <p className="text-ms-cream/70 mt-5 leading-[1.7] text-[15px]">
              Get the date before the public does — plus quiet invites to seasonal one-offs and partner-night events.
            </p>
          </div>
          {/* Right — form */}
          <div>
            {state === "success" ? (
              <div className="bg-ms-cream/5 border border-ms-brass/30 rounded-lg p-6" data-testid="newsletter-cta-success">
                <p className="label-eyebrow text-ms-brass text-[10px]">You're in</p>
                <p className="text-ms-cream mt-2 text-lg leading-[1.5]">Watch your inbox for new mysteries.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-3" noValidate>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full bg-ms-cream/5 border border-ms-cream/15 rounded-md px-4 py-3.5 text-ms-cream placeholder-ms-cream/40 focus:outline-none focus:border-ms-brass transition-colors"
                  data-testid="newsletter-cta-name"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Envelope size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ms-cream/50" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="w-full bg-ms-cream/5 border border-ms-cream/15 rounded-md pl-11 pr-4 py-3.5 text-ms-cream placeholder-ms-cream/40 focus:outline-none focus:border-ms-brass transition-colors"
                      data-testid="newsletter-cta-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    className="inline-flex items-center justify-center gap-2 bg-ms-brass text-ms-ink hover:bg-ms-brass-hi disabled:opacity-50 transition-all rounded-md px-7 py-3.5 label-eyebrow text-xs whitespace-nowrap"
                    data-testid="newsletter-cta-submit"
                  >
                    {state === "sending" ? "Joining…" : "Join the list"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
                {state === "error" && (
                  <p className="text-[#FFB4A2] text-xs mt-1" data-testid="newsletter-cta-error">Something went wrong — please double-check your email and try again.</p>
                )}
                <p className="text-ms-cream/40 text-[11px] mt-2">One email when a new mystery drops. Unsubscribe with one click.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
